import React, { useEffect } from 'react';
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
import { trackEngagement } from '../utils/analytics';

interface PerformanceMonitorProps {
  onVitalsUpdate?: (vitals: any) => void;
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ onVitalsUpdate }) => {
  useEffect(() => {
    // Track Core Web Vitals
    const trackVital = (metric: any) => {
      // Send to analytics using our utility
      trackEngagement(`web_vital_${metric.name.toLowerCase()}`, Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value));

      // Callback for custom handling
      if (onVitalsUpdate) {
        onVitalsUpdate(metric);
      }

      // Console log for development
      console.log('Web Vital:', metric);
    };

    // Measure Core Web Vitals
    getCLS(trackVital);
    getFID(trackVital);
    getFCP(trackVital);
    getLCP(trackVital);
    getTTFB(trackVital);

    // Track page load performance
    if (typeof window !== 'undefined' && 'performance' in window) {
      window.addEventListener('load', () => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        if (navigation) {
          const metrics = {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            firstByte: navigation.responseStart - navigation.requestStart,
            domInteractive: navigation.domInteractive - navigation.fetchStart,
          };

          // Send to analytics
          if ((window as any).gtag) {
            Object.entries(metrics).forEach(([key, value]) => {
              (window as any).gtag('event', 'timing_complete', {
                name: key,
                value: Math.round(value),
              });
            });
          }
        }
      });
    }

    // Track resource loading performance
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            if (resource.duration > 1000) { // Log slow resources
              console.warn('Slow resource detected:', {
                name: resource.name,
                duration: resource.duration,
                size: resource.transferSize,
              });
            }
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });

      return () => observer.disconnect();
    }
  }, [onVitalsUpdate]);

  return null; // This component doesn't render anything
};

export default PerformanceMonitor;
